/**
 * CameraController - Handles camera positioning and animation
 */
import * as THREE from 'three';

export class CameraController {
  constructor(camera, controls) {
    this.camera = camera;
    this.controls = controls;
  }

  /**
   * Center camera on a set of waypoint meshes
   * @param {Array} waypointMeshes - Array of waypoint mesh groups
   */
  centerOnWaypoints(waypointMeshes) {
    if (waypointMeshes.length === 0) return;

    // Calculate bounding box
    const box = new THREE.Box3();
    waypointMeshes.forEach(mesh => box.expandByObject(mesh));

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = Math.max(maxDim * 0.7, 0.4);

    // Animate to new camera position
    const newPosition = new THREE.Vector3(
      center.x + distance,
      center.y + distance,
      center.z + distance
    );

    this.animateTo(newPosition, center);
  }

  /**
   * Focus camera on a specific waypoint
   * @param {THREE.Object3D} mesh - Waypoint mesh to focus on
   */
  focusOnMesh(mesh) {
    if (!mesh) return;

    const targetPosition = mesh.position.clone();
    const viewDistance = 0.8;
    
    // Get current camera direction
    const currentTarget = this.controls.target.clone();
    const currentPosition = this.camera.position.clone();
    const direction = currentPosition.sub(currentTarget).normalize();
    
    // Set new camera position relative to the mesh
    const newPosition = targetPosition.clone().add(direction.multiplyScalar(viewDistance));
    
    this.animateTo(newPosition, targetPosition);
  }

  /**
   * Animate camera to new position and target
   * @param {THREE.Vector3} newPosition - New camera position
   * @param {THREE.Vector3} newTarget - New camera target
   * @param {number} duration - Animation duration in ms
   */
  animateTo(newPosition, newTarget, duration = 500) {
    const startPosition = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease in-out cubic
      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      this.camera.position.lerpVectors(startPosition, newPosition, eased);
      this.controls.target.lerpVectors(startTarget, newTarget, eased);
      this.controls.update();

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }

  /**
   * Set camera position immediately
   * @param {THREE.Vector3} position - Camera position
   * @param {THREE.Vector3} target - Look-at target
   */
  setPosition(position, target) {
    this.camera.position.copy(position);
    this.controls.target.copy(target);
    this.controls.update();
  }
}
